import React, {useContext, useCallback} from 'react';
import {autoReplenish} from '../../../../settings';
import AutoRenewSection from './AutoRenewSection';
import {getExpiryDate, isAPassNotATicket} from '../../../../helperFunctions';
import {Context} from '../../../../context';

const showAutoRenewSection = (fareProduct, ticketUnexpired, account) => {
  if (!autoReplenish) {
    return false;
  }
  if (!ticketUnexpired) {
    return false;
  }
  if (!isAPassNotATicket(fareProduct.serial)) {
    return false;
  }

  const alreadyRenewedProfile = account?.autoPurchaseProfiles?.find(
    ({ticketId, cancelledDate, cancelledReason, __typename}) =>
      __typename === 'AutoRenewProfile' &&
      ticketId.toString() === fareProduct.id &&
      cancelledDate !== null &&
      cancelledReason === null
  );

  return alreadyRenewedProfile ? false : true;
};

const FareProductTables = props => {
  const {
    account,
    getTicketName,
    localeText,
    disableAutoRenewProfile,
    createAutoRenewProfile,
    setSnackBarMessage
  } = useContext(Context);
  const {fareProducts, getFareProductStatuses} = props;

  const getAutoRenewProfileFromFareProduct = useCallback(
    fareProduct =>
      account?.autoPurchaseProfiles?.find(
        ({cancelledDate, ticketId}) =>
          cancelledDate === null && ticketId == fareProduct.id
      ),
    [account]
  );

  const autoRenewEnabled = fareProduct =>
    getAutoRenewProfileFromFareProduct(fareProduct) ? true : false;

  const billingProfile = fareProduct => {
    const arp = getAutoRenewProfileFromFareProduct(fareProduct);
    if (!arp) return null;
    return account.paymentMethods.find(pm => pm.id === arp.billingProfileId);
  };

  const toggleAutoRenew = fareProduct => {
    if (autoRenewEnabled(fareProduct)) {
      const autoRenewProfile = getAutoRenewProfileFromFareProduct(fareProduct);
      return disableAutoRenewProfile(autoRenewProfile.id);
    }

    if (
      account.paymentMethods.find(({processor}) => processor !== 'STORED_VALUE')
        .length === 0
    ) {
      setSnackBarMessage('You have no payment methods'); // kbfu
      return;
    }
    const autoRenewProfile = {
      billingProfileId: account.paymentMethods.find(
        ({processor}) => processor !== 'STORED_VALUE'
      )?.id,
      productSku: fareProduct.productSku,
      ticketId: fareProduct.id
    };
    return createAutoRenewProfile(autoRenewProfile);
  };

  const changeBillingProfile = (event, fareProduct) => {
    const autoRenewProfile = {
      billingProfileId: event.target.value,
      productSku: fareProduct.productSku,
      ticketId: fareProduct.id
    };
    const currentAutoRenewProfile = account.autoPurchaseProfiles.find(
      ({ticketId, cancelledDate}) => {
        if (!ticketId) {
          return null;
        }
        return (
          ticketId.toString() === fareProduct.id.toString() &&
          cancelledDate === null
        );
      }
    );
    disableAutoRenewProfile(currentAutoRenewProfile?.id).then(ret =>
      createAutoRenewProfile(autoRenewProfile)
    );
  };

  return (
    <React.Fragment>
      <table
        //* Fare Products - DESKTOP View
        className='hidden sm:table'
        id='fare-products-table'
      >
        <tbody>
          {fareProducts.map((fareProduct, i) => {
            const {ticketActiveStatus, ticketUnexpired} =
              getFareProductStatuses(fareProduct);

            return (
              <tr className='hover:bg-lightGray2' key={i}>
                <td className=''>{getTicketName(fareProduct.productId)}</td>
                <td className='pl-4'>
                  exp{' '}
                  {getExpiryDate(
                    fareProduct.validToDateTime,
                    fareProduct.sunsetDateTime
                  )}
                </td>
                <td className='pl-4'>{ticketActiveStatus.toUpperCase()}</td>
                {showAutoRenewSection(
                  fareProduct,
                  ticketUnexpired,
                  account
                ) && (
                  <td className={`pl-4`}>
                    <AutoRenewSection
                      {...{
                        autoRenewEnabled,
                        fareProduct,
                        toggleAutoRenew,
                        changeBillingProfile,
                        billingProfile,
                        idKey: i
                      }}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <table
        //* Fare Products - MOBILE View
        className='sm:hidden w-full'
      >
        {fareProducts.map((fareProduct, i) => {
          const {ticketActiveStatus, ticketUnexpired} =
            getFareProductStatuses(fareProduct);
          return (
            <tbody key={i} className='border-t first:border-t-0'>
              <tr>
                <td className='px-2 pt-2 font-semibold align-top'>
                  {localeText('rw_title_pass', 'Pass')}
                </td>
                <td className='px-2 pt-2 align-top'>
                  {getTicketName(fareProduct.productId)}
                </td>
              </tr>
              <tr>
                <td className='px-2 font-semibold align-top'>
                  {localeText('rw_title_exp', 'Exp')}
                </td>
                <td className='px-2 align-top'>
                  {getExpiryDate(
                    fareProduct.validToDateTime,
                    fareProduct.sunsetDateTime
                  )}
                </td>
              </tr>
              <tr>
                <td className='px-2 font-semibold align-top'>
                  {localeText('rw_title_status', 'Status')}
                </td>
                <td className='px-2 align-top'>
                  {ticketActiveStatus.toUpperCase()}
                </td>
              </tr>
              {showAutoRenewSection(fareProduct, ticketUnexpired, account) && (
                <tr>
                  <td className='px-2 pt-1 pb-3' colSpan='2'>
                    <AutoRenewSection
                      {...{
                        autoRenewEnabled,
                        fareProduct,
                        toggleAutoRenew,
                        changeBillingProfile,
                        billingProfile,
                        idKey: i
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          );
        })}
      </table>
    </React.Fragment>
  );
};

export default FareProductTables;
